import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react'

import Modal from '@Modal/V3/Modal'

import Input from '@/Form/V2/Input'

import FormStatus from '@/Form/FormStatus'

import Loader from '@Pure/Loader'

import request from '@function/ajax/request'
import bemit   from '@function/bemit'

import BatchPaymentCheckStore from '@Store/BatchPaymentCheck'

import FormsStore from '@Store/Forms'

import { num, formatNum } from '@root/utility'

const FORM_NAME = 'CheckDeposit';

const { app, moment } = window;

export default function CheckDepositModal ({ onClose, ...props })
{
	const [status, setStatus] = useState({ });
	const [loading, setLoading] = useState(false);
	const [auto_closing, setAutoClosing] = useState(0);
	const [created, setCreated] = useState(false);
	const [data, setData] = useState();

	const form_data = FormsStore.useState(s => s[FORM_NAME] || { });

	const checks_ids = BatchPaymentCheckStore.useState(function (store)
	{
		return Object.keys(store).filter(id => store[id] === true);
	});

	const _ref_ctrl = useRef();
	const _ref_busy = useRef();

	useEffect(function ()
	{
		if (!auto_closing) return;

		const timeout = window.setTimeout(function ()
		{
			setAutoClosing(0);
		},
		auto_closing);

		return () => window.clearTimeout(timeout);
	},
	[auto_closing, onClose]);

// réinitialise la modale une fois masquée pour éviter
// de modifier ce qui est affiché en pleine animation d'ouverture/fermeture
	const handleModalHidden = useCallback(function (params)
	{
		setCreated(false);
		setStatus({ });
	},
	[]);

	const modifiers = useMemo(function ()
	{
		return props.bemit || ['small'];
	},
	[props.bemit]);

	const prevent_close = loading === 'creating' || auto_closing > 0;

	useEffect(function ()
	{
		if (!props.open) return;

		(async function fetchChecks ()
		{
			setLoading('fetching');

			try
			{
				var response = await request('/user-payments/get-for-deposit')
				.config({ })
				.post(
				{
					checks: checks_ids
				});
			}
			catch (update)
			{
				return setStatus(update);
			}
			finally
			{
				setLoading(false);
			}

			BatchPaymentCheckStore.update(s => { });

			setData(response.payments || []);
		})();
	},
	[props.open, checks_ids]);

	const validate = useCallback(function ()
	{
		if (_ref_busy.current) return;
		_ref_busy.current = true;

		setLoading('creating');

		setStatus({ });

		const controller = new AbortController();
		_ref_ctrl.current = controller;

		(async function update ()
		{
			try
			{
				var response = await request('/check-batch/new')
				.config({ controller })
				.post(
				{
					...form_data,
					checks: checks_ids,
				});
			}
			catch (update)
			{
				return setStatus(update);
			}
			finally
			{
				_ref_busy.current = false;
				setLoading(false);
			}

			BatchPaymentCheckStore.update(s => { });

			setCreated(true);
			setAutoClosing(1000);
			setStatus(response);

			app.emit('UpdateUserPaymentsList');
		})();

		return () => { _ref_ctrl.current?.abort?.(); _ref_busy.current = false; };
	},
	[form_data, checks_ids]);

	const footer = created
	? <FormStatus { ...status } />
	:
	(
		<>
			<FormStatus { ...status } />

			<button type="button" aria-label="Fermer"
				className={ bemit('c-btn', ['green']) }
				onClick={ validate }>
				Créer
			</button>

			<button type="button" aria-label="Fermer"
				className={ props['close-classes'] || bemit('c-btn', ['grey']) }
				onClick={ exit }>
				Fermer
			</button>

			{
				loading ?
				(
					<Loader className="c-loader" bemit={ ['primary', 'bg-white'] }
						label="Mise à jour..."
					/>
				)
				: undefined
			}

		</>
	);

	const details = (created ? [] : data || []).map(function (payment)
	{
		return (
			<div className="c-dashboard__row" key={ payment.id }>

				<div className="c-dashboard__cell Payment-date">
					{ moment(payment.createdAt).format('DD/MM/YYYY') }
				</div>

				<div className="c-dashboard__cell Payment-name">
					{ payment.name }
				</div>

				<div className="c-dashboard__cell Payment-number">
					{ payment.number }
				</div>

				<div className="c-dashboard__cell Payment-amount">
					{ payment.amount }€
				</div>

			</div>
		);
	});

	const total = (created ? [] : data || []).reduce(function (total, payment)
	{
		return num(total + num(payment.amount));
	},
	0);

	const header_row = details.length > 0 ?
	(
		<div className="c-dashboard__row is-header-row">

			<div className="c-dashboard__cell Payment-date">
				Date
			</div>

			<div className="c-dashboard__cell Payment-name">
				Nom
			</div>

			<div className="c-dashboard__cell Payment-number">
				N° de chèque
			</div>

			<div className="c-dashboard__cell Payment-amount">
				Montant
			</div>

		</div>
	)
	: undefined;

	const total_row = details.length > 1 ?
	(
		<div className="c-dashboard__row is-transparent">

			<div className="c-dashboard__cell Payment-date" />

			<div className="c-dashboard__cell Payment-name" />

			<div className="c-dashboard__cell Payment-number">
				<div className="u-full-width u-text-right">
					Total&nbsp;:
				</div>
			</div>

			<div className="c-dashboard__cell Payment-amount">
				<strong>{ formatNum(total) }€</strong>
			</div>

		</div>
	)
	: undefined;

	const content = created ?
	(
		<FormStatus info="Bordereau créé avec succès." />
	)
	:
	(
		<>
			<div className={ bemit('c-dashboard', ['payment']) +
				' u-full-width u-margin-bottom-10' }>
				{ header_row }
				{ details }
				{ total_row }
			</div>

			{
				status.fields?.checks?.error ?
				(
					<FormStatus error={ status.fields.checks.error } />
				)
				: undefined
			}

			<Input name="number" label="N° de bordereau"
				form={ FORM_NAME }
				bemit={ ['25%'] }
				error={ status.fields?.['number']?.error }
			/>

			<div className="u-margin-top-5" />
		</>
	);

	return (
		<Modal open={ props.open === true } onClose={ onClose }
			bemit={ modifiers } trg={ props.trg } footer={ footer }
			title="Remise de chèques"
			prevent-close={ prevent_close }
			afterHidden={ handleModalHidden }>

			{ content }

		</Modal>
	);

	function exit (e)
	{
		_ref_ctrl.current?.abort?.();
		setAutoClosing(0);
		onClose(e);
	}
}